import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './Superadmin.module.css';
import AIInstructies from './AIInstructies';
import LogoUpload from '../components/LogoUpload';

const LICENTIE_LABELS = { basis:'Basis', professioneel:'Professioneel', enterprise:'Enterprise', onderwijs:'Onderwijs' };
const HIERARCHIE_LABELS = { plat:'Org → User', locatie:'Org → Loc → User', afdeling:'Org → Loc → Afd → User' };

const VELD_LABELS = {
  ademfrequentie: 'Ademfrequentie (/min)',
  o2_saturatie:   'O2-saturatie (%)',
  hartslag:       'Hartslag (/min)',
  bloeddruk_sys:  'Bloeddruk systolisch (mmHg)',
  bloeddruk_dia:  'Bloeddruk diastolisch (mmHg)',
  temperatuur:    'Temperatuur (°C)',
  bloedsuiker:    'Bloedsuiker (mmol/L)',
  pijnscore:      'Pijnscore (NRS)',
};

function DrempelRij({ d, heeftOrg, onOpslaan, onReset }) {
  const [bewerken, setBewerken] = useState(false);
  const [form, setForm] = useState({
    kritisch_laag: d.kritisch_laag ?? '',
    laag:          d.laag ?? '',
    hoog:          d.hoog ?? '',
    kritisch_hoog: d.kritisch_hoog ?? '',
  });

  function handleOpslaan() {
    onOpslaan(form);
    setBewerken(false);
  }

  return (
    <tr>
      <td style={{fontWeight:600,color:'#1A1A2E',fontSize:13}}>{VELD_LABELS[d.veld] || d.veld}</td>
      {bewerken ? (
        <>
          {['kritisch_laag','laag','hoog','kritisch_hoog'].map(k => (
            <td key={k}>
              <input type="number" step="0.1" value={form[k]}
                onChange={e => setForm({...form,[k]:e.target.value})}
                style={{width:70,padding:'4px 6px',border:'1.5px solid #D0D7E3',borderRadius:6,fontSize:13}} />
            </td>
          ))}
        </>
      ) : (
        <>
          <td style={{textAlign:'center',color:'#CC0000',fontWeight:600}}>{d.kritisch_laag ?? '—'}</td>
          <td style={{textAlign:'center',color:'#C65A00'}}>{d.laag ?? '—'}</td>
          <td style={{textAlign:'center',color:'#C65A00'}}>{d.hoog ?? '—'}</td>
          <td style={{textAlign:'center',color:'#CC0000',fontWeight:600}}>{d.kritisch_hoog ?? '—'}</td>
        </>
      )}
      <td>
        <span style={{
          background: d.bron === 'organisatie' ? '#D4EDDA' : '#EEF2FF',
          color:      d.bron === 'organisatie' ? '#1A6B3C' : '#2D2B6B',
          padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:600
        }}>
          {d.bron === 'organisatie' ? 'Organisatie' : 'Platform'}
        </span>
      </td>
      <td>
        <div style={{display:'flex',gap:4}}>
          {bewerken ? (
            <>
              <button onClick={handleOpslaan}
                style={{padding:'4px 10px',background:'#3B5FC0',color:'#fff',border:'none',borderRadius:6,fontSize:12,cursor:'pointer'}}>
                Opslaan
              </button>
              <button onClick={() => setBewerken(false)}
                style={{padding:'4px 10px',background:'#fff',border:'1.5px solid #D0D7E3',borderRadius:6,fontSize:12,cursor:'pointer'}}>
                Annuleren
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setBewerken(true)}
                style={{padding:'4px 10px',background:'#fff',border:'1.5px solid #D0D7E3',borderRadius:6,fontSize:12,cursor:'pointer'}}>
                Bewerken
              </button>
              {heeftOrg && d.bron === 'organisatie' && (
                <button onClick={onReset}
                  style={{padding:'4px 10px',background:'#fff',border:'1.5px solid #FFCCCC',color:'#CC0000',borderRadius:6,fontSize:12,cursor:'pointer'}}>
                  Reset
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function Superadmin() {
  const navigate = useNavigate();
  const [tab, setTab]               = useState('organisaties');
  const [shadowLaden, setShadowLaden] = useState(null);

  // Modules
  const [modulesLijst, setModulesLijst]   = useState([]);
  const [modulesOrg, setModulesOrg]       = useState('');
  const [orgModules, setOrgModules]       = useState([]);
  const [modulesLaden, setModulesLaden]   = useState(false);

  async function laadOrgModules(orgId) {
    if (!orgId) return;
    setModulesLaden(true);
    try {
      const res = await axios.get(`/api/modules/${orgId}`);
      setOrgModules(res.data.modules || []);
    } catch { toast.error('Modules laden mislukt.'); }
    finally { setModulesLaden(false); }
  }

  async function toggleModule(orgId, code, huidigActief) {
    try {
      await axios.post(`/api/modules/${orgId}`, { module_code: code, actief: !huidigActief });
      setOrgModules(prev => prev.map(m => m.code === code ? { ...m, actief: !huidigActief } : m));
      toast.success('Module bijgewerkt.');
    } catch { toast.error('Bijwerken mislukt.'); }
  }

  async function pasLicentieToe(orgId, licentie) {
    try {
      await axios.post(`/api/modules/${orgId}/licentie`, { licentie_type: licentie });
      await laadOrgModules(orgId);
      toast.success(`Modules ingesteld voor ${licentie}.`);
    } catch { toast.error('Licentie toepassen mislukt.'); }
  }

  useEffect(() => {
    if (tab === 'modules') {
      axios.get('/api/modules').then(r => setModulesLijst(r.data.modules || [])).catch(() => {});
    }
  }, [tab]);

  // Platform updates
  const [updateLijst, setUpdateLijst]     = useState([]);
  const [updateLaden, setUpdateLaden]     = useState(false);
  const [updateForm, setUpdateForm]       = useState({ versie:'', titel:'', omschrijving:'', datum:'' });
  const [updateBewerkId, setUpdateBewerkId] = useState(null);
  const [toonUpdateForm, setToonUpdateForm] = useState(false);

  async function laadUpdates() {
    setUpdateLaden(true);
    try {
      const res = await axios.get('/api/platform-updates/alle');
      setUpdateLijst(res.data.updates || []);
    } catch { toast.error('Updates laden mislukt.'); }
    finally { setUpdateLaden(false); }
  }

  async function handleUpdateOpslaan() {
    if (!updateForm.versie || !updateForm.titel || !updateForm.omschrijving || !updateForm.datum) {
      return toast.error('Alle velden zijn verplicht.');
    }
    try {
      if (updateBewerkId) {
        await axios.put(`/api/platform-updates/${updateBewerkId}`, updateForm);
        toast.success('Update bijgewerkt.');
      } else {
        await axios.post('/api/platform-updates', updateForm);
        toast.success('Update aangemaakt.');
      }
      setToonUpdateForm(false);
      setUpdateBewerkId(null);
      setUpdateForm({ versie:'', titel:'', omschrijving:'', datum:'' });
      laadUpdates();
    } catch (err) { toast.error(err.response?.data?.error || 'Opslaan mislukt.'); }
  }

  async function handleUpdateVerwijderen(id) {
    if (!window.confirm('Update verwijderen?')) return;
    try {
      await axios.delete(`/api/platform-updates/${id}`);
      toast.success('Update verwijderd.');
      laadUpdates();
    } catch { toast.error('Verwijderen mislukt.'); }
  }

  // Drempelwaarden
  const [drempelOrg, setDrempelOrg]       = useState('');
  const [drempelwaarden, setDrempelwaarden] = useState([]);
  const [drempelLaden, setDrempelLaden]   = useState(false);
  const [organisaties, setOrganisaties] = useState([]);
  const [rapportages, setRapportages]   = useState(null);
  const [laden, setLaden]           = useState(true);
  const [toonModal, setToonModal]   = useState(false);
  const [geselecteerde, setGeselecteerde] = useState(null);
  const [banner, setBanner]         = useState({ actief: false, tekst: '' });
  const [bannerLaden, setBannerLaden] = useState(false);
  const [apiSleutel, setApiSleutel] = useState('');
  const [apiOrg, setApiOrg]         = useState(null);

  // Gebruikers tab
  const [alleGebruikers, setAlleGebruikers] = useState([]);
  const [gebruikersLaden, setGebruikersLaden] = useState(false);
  const [orgFilter, setOrgFilter]   = useState('');
  const [zoekFilter, setZoekFilter] = useState('');
  const [resetModal, setResetModal] = useState(null);
  const [nieuwWachtwoord, setNieuwWachtwoord] = useState('');

  const leegForm = {
    naam:'', slug:'', licentie_type:'basis', hierarchie:'plat',
    max_gebruikers:10, max_outputs_pm:500, max_locaties:1,
    primaire_kleur:'#1B4F8A', licentie_start:'', licentie_einde:'', bewaartermijn:90,
  };
  const [form, setForm] = useState(leegForm);

  useEffect(() => { laadData(); }, []);

  useEffect(() => {
    if (tab === 'updates') laadUpdates();
  }, [tab]);

  useEffect(() => {
    if (tab === 'drempelwaarden') {
      laadDrempelwaarden(drempelOrg);
    }
  }, [tab]);

  async function laadData() {
    setLaden(true);
    try {
      const [orgRes, rapRes, instRes] = await Promise.all([
        axios.get('/api/organisaties'),
        axios.get('/api/organisaties/rapportages'),
        axios.get('/api/organisaties/platform-instellingen'),
      ]);
      setOrganisaties(orgRes.data.organisaties);
      setRapportages(rapRes.data);
      setBanner({
        actief: instRes.data.instellingen.banner_actief === '1',
        tekst:  instRes.data.instellingen.banner_tekst || '',
      });
    } catch { toast.error('Data laden mislukt.'); }
    finally { setLaden(false); }
  }

  async function laadGebruikers() {
    setGebruikersLaden(true);
    try {
      const params = {};
      if (orgFilter) params.organisatie_id = orgFilter;
      if (zoekFilter) params.zoek = zoekFilter;
      const res = await axios.get('/api/superadmin/gebruikers', { params });
      setAlleGebruikers(res.data.gebruikers);
    } catch { toast.error('Gebruikers laden mislukt.'); }
    finally { setGebruikersLaden(false); }
  }

  useEffect(() => {
    if (tab === 'gebruikers') laadGebruikers();
  }, [tab, orgFilter, zoekFilter]);

  async function handle2FAReset(id) {
    if (!window.confirm('2FA resetten voor deze gebruiker?')) return;
    try {
      await axios.post(`/api/superadmin/gebruikers/${id}/2fa-reset`);
      toast.success('2FA gereset. Gebruiker moet 2FA opnieuw instellen.');
      laadGebruikers();
    } catch (err) { toast.error(err.response?.data?.error || 'Reset mislukt.'); }
  }

  async function handleGebruikerActief(id, actief) {
    try {
      await axios.patch(`/api/superadmin/gebruikers/${id}/actief`, { actief });
      toast.success(actief ? 'Gebruiker geactiveerd.' : 'Gebruiker gedeactiveerd.');
      laadGebruikers();
    } catch (err) { toast.error(err.response?.data?.error || 'Mislukt.'); }
  }

  async function handleWachtwoordReset(e) {
    e.preventDefault();
    try {
      await axios.post(`/api/superadmin/gebruikers/${resetModal.id}/wachtwoord-reset`, { wachtwoord: nieuwWachtwoord });
      toast.success('Wachtwoord gereset.');
      setResetModal(null);
      setNieuwWachtwoord('');
    } catch (err) { toast.error(err.response?.data?.error || 'Reset mislukt.'); }
  }

  function openAanmaken() { setGeselecteerde(null); setForm(leegForm); setToonModal(true); }
  function openBewerken(org) {
    setGeselecteerde(org);
    setForm({
      naam: org.naam, slug: org.slug, licentie_type: org.licentie_type,
      hierarchie: org.hiërarchie, max_gebruikers: org.max_gebruikers,
      max_outputs_pm: org.max_outputs_pm, max_locaties: org.max_locaties,
      primaire_kleur: org.primaire_kleur || '#1B4F8A',
      licentie_start: org.licentie_start ? org.licentie_start.slice(0,10) : '',
      licentie_einde: org.licentie_einde ? org.licentie_einde.slice(0,10) : '',
      bewaartermijn: org.bewaartermijn || 90,
    });
    setToonModal(true);
  }

  async function handleOpslaan(e) {
    e.preventDefault();
    try {
      if (geselecteerde) {
        await axios.put(`/api/organisaties/${geselecteerde.id}`, form);
        toast.success('Organisatie bijgewerkt.');
      } else {
        await axios.post('/api/organisaties', form);
        toast.success('Organisatie aangemaakt.');
      }
      setToonModal(false);
      laadData();
    } catch (err) { toast.error(err.response?.data?.error || 'Opslaan mislukt.'); }
  }

  async function laadDrempelwaarden(orgId) {
    setDrempelLaden(true);
    try {
      const params = orgId ? { organisatie_id: orgId } : {};
      const res = await axios.get('/api/drempelwaarden', { params });
      setDrempelwaarden(res.data.drempelwaarden);
    } catch { toast.error('Drempelwaarden laden mislukt.'); }
    finally { setDrempelLaden(false); }
  }

  async function handleDrempelOpslaan(veld, waarden) {
    try {
      await axios.post('/api/drempelwaarden', {
        organisatie_id: drempelOrg || null,
        veld, ...waarden
      });
      toast.success('Drempelwaarde opgeslagen.');
      laadDrempelwaarden(drempelOrg);
    } catch { toast.error('Opslaan mislukt.'); }
  }

  async function handleDrempelReset(veld) {
    try {
      await axios.post('/api/drempelwaarden/reset', {
        organisatie_id: drempelOrg || null,
        veld,
      });
      toast.success('Teruggesteld naar platformstandaard.');
      laadDrempelwaarden(drempelOrg);
    } catch { toast.error('Reset mislukt.'); }
  }

  async function handleShadowLogin(org) {
    setShadowLaden(org.id);
    try {
      const res = await axios.post(`/api/superadmin/organisaties/${org.id}/shadow-login`);
      const { token } = res.data;
      // Bewaar huidig superadmin token om later terug te keren
      const huidigToken = localStorage.getItem('alyden_token');
      localStorage.setItem('alyden_shadow_return_token', huidigToken);
      // Sla shadow token op en verifieer
      localStorage.setItem('alyden_token', token);
      const check = localStorage.getItem('alyden_token');
      if (check !== token) {
        localStorage.setItem('alyden_token', token);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Navigeer via replace — volledige herstart van de app
      // Kleine vertraging zodat localStorage zeker gesynchroniseerd is
      setTimeout(() => window.location.replace('/dashboard'), 150);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Shadow access mislukt.');
    } finally {
      setShadowLaden(null);
    }
  }

  async function handleToggleActief(org) {
    try {
      await axios.put(`/api/organisaties/${org.id}`, { actief: org.actief ? 0 : 1 });
      toast.success(org.actief ? 'Gedeactiveerd.' : 'Geactiveerd.');
      laadData();
    } catch { toast.error('Status wijzigen mislukt.'); }
  }

  async function handleApiSleutel(orgId) {
    if (!apiSleutel) return toast.error('Voer een API-sleutel in.');
    try {
      await axios.post(`/api/organisaties/${orgId}/api-sleutel`, { api_key: apiSleutel });
      toast.success('API-sleutel opgeslagen.');
      setApiSleutel(''); setApiOrg(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Opslaan mislukt.'); }
  }

  async function handleBanner() {
    setBannerLaden(true);
    try {
      await axios.post('/api/organisaties/platform/banner', banner);
      toast.success('Banner bijgewerkt.');
    } catch { toast.error('Instellen mislukt.'); }
    finally { setBannerLaden(false); }
  }

  if (laden) return <div className={styles.laden}>Laden...</div>;

  return (
    <div className={styles.pagina}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.titel}>Superadmin</h1>
          <p className={styles.subtitel}>Platformbeheer — alleen voor platformeigenaar</p>
        </div>
      </div>

      <div className={styles.tabs}>
        {[
          { id:'organisaties', label:'Organisaties' },
          { id:'gebruikers',   label:'Gebruikers' },
          { id:'rapportages',  label:'Rapportages' },
          { id:'drempelwaarden',  label:'Drempelwaarden' },
          { id:'ai-instructies',   label:'AI Instructies' },
          { id:'modules',         label:'Modules' },
          { id:'updates',         label:'Updates' },
          { id:'platform',        label:'Platform' },
        ].map(t => (
          <button key={t.id}
            className={`${styles.tab} ${tab === t.id ? styles.actief : ''}`}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ORGANISATIES ── */}
      {tab === 'organisaties' && (
        <div>
          <div className={styles.tabHeader}>
            <span className={styles.teller}>{organisaties.length} organisatie(s)</span>
            <button className={styles.knopPrimair} onClick={openAanmaken}>+ Organisatie toevoegen</button>
          </div>
          {rapportages?.verlopenBinnenkort?.length > 0 && (
            <div className={styles.waarschuwingBalk}>
              ⚠ {rapportages.verlopenBinnenkort.length} licentie(s) verlopen binnenkort:
              {rapportages.verlopenBinnenkort.map(o => (
                <span key={o.id} className={styles.verloopOrg}>
                  {o.naam} ({new Date(o.licentie_einde).toLocaleDateString('nl-NL')})
                </span>
              ))}
            </div>
          )}
          <div className={styles.tabelWrap}>
            <table className={styles.tabel}>
              <thead><tr>
                <th>Organisatie</th><th>Licentie</th><th>Hiërarchie</th>
                <th>Gebruikers</th><th>Outputs</th><th>Laatste activiteit</th>
                <th>Status</th><th>Acties</th>
              </tr></thead>
              <tbody>
                {organisaties.map(org => (
                  <tr key={org.id}>
                    <td>
                      <div className={styles.orgNaam}>
                        <div className={styles.orgKleurBolletje} style={{ background: org.primaire_kleur || '#1B4F8A' }} />
                        <div>
                          <div className={styles.orgNaamTekst}>{org.naam}</div>
                          <div className={styles.orgSlug}>{org.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className={styles.licentieBadge}>{LICENTIE_LABELS[org.licentie_type]}</span></td>
                    <td className={styles.klein}>{HIERARCHIE_LABELS[org.hiërarchie]}</td>
                    <td className={styles.getal}>{org.aantal_gebruikers} / {org.max_gebruikers}</td>
                    <td className={styles.getal}>{org.totaal_outputs}</td>
                    <td className={styles.datum}>{org.laatste_activiteit ? new Date(org.laatste_activiteit).toLocaleDateString('nl-NL') : '—'}</td>
                    <td>{org.actief ? <span className={styles.badgeGroen}>Actief</span> : <span className={styles.badgeGrijs}>Inactief</span>}</td>
                    <td>
                      <div className={styles.actieKnoppen}>
                        <button className={styles.knopKlein} onClick={() => openBewerken(org)}>Bewerken</button>
                        <button className={styles.knopKlein} onClick={() => { setApiOrg(org); setApiSleutel(''); }}>API-sleutel</button>
                        <button className={styles.knopKlein}
                          style={{ color:'#7B2D8B', borderColor:'#E8C4F0' }}
                          onClick={() => handleShadowLogin(org)}
                          disabled={shadowLaden === org.id}>
                          {shadowLaden === org.id ? '...' : '👁 Shadow'}
                        </button>
                        <button className={`${styles.knopKlein} ${!org.actief ? styles.groen : styles.rood}`} onClick={() => handleToggleActief(org)}>
                          {org.actief ? 'Deactiveren' : 'Activeren'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── GEBRUIKERS ── */}
      {tab === 'gebruikers' && (
        <div>
          <div className={styles.filterBalk}>
            <select value={orgFilter} onChange={e => setOrgFilter(e.target.value)} className={styles.filterSelect}>
              <option value="">Alle organisaties</option>
              {organisaties.map(o => <option key={o.id} value={o.id}>{o.naam}</option>)}
            </select>
            <input type="text" placeholder="Zoeken op naam of e-mail..."
              value={zoekFilter} onChange={e => setZoekFilter(e.target.value)}
              className={styles.zoekInput} />
          </div>
          {gebruikersLaden ? (
            <div className={styles.laadTekst}>Laden...</div>
          ) : (
            <div className={styles.tabelWrap}>
              <table className={styles.tabel}>
                <thead><tr>
                  <th>Naam / E-mail</th><th>Organisatie</th><th>Rol</th>
                  <th>Status</th><th>2FA</th><th>Laatste login</th><th>Acties</th>
                </tr></thead>
                <tbody>
                  {alleGebruikers.length === 0 && (
                    <tr><td colSpan={7} className={styles.leeg}>Geen gebruikers gevonden.</td></tr>
                  )}
                  {alleGebruikers.map(g => (
                    <tr key={g.id}>
                      <td>
                        <div className={styles.naamCell}>
                          <span className={styles.naam}>{g.naam || '—'}</span>
                          <span className={styles.email}>{g.email}</span>
                        </div>
                      </td>
                      <td className={styles.klein}>{g.organisatie_naam}</td>
                      <td><span className={styles.licentieBadge}>{g.rol_naam}</span></td>
                      <td>{g.actief ? <span className={styles.badgeGroen}>Actief</span> : <span className={styles.badgeGrijs}>Inactief</span>}</td>
                      <td>{g.twofa_actief ? <span className={styles.badgeGroen}>✓</span> : <span className={styles.badgeGrijs}>—</span>}</td>
                      <td className={styles.datum}>{g.laatste_login ? new Date(g.laatste_login).toLocaleDateString('nl-NL') : '—'}</td>
                      <td>
                        <div className={styles.actieKnoppen}>
                          <button className={styles.knopKlein} onClick={() => handle2FAReset(g.id)}>2FA reset</button>
                          <button className={styles.knopKlein} onClick={() => { setResetModal(g); setNieuwWachtwoord(''); }}>Wachtwoord</button>
                          <button className={`${styles.knopKlein} ${!g.actief ? styles.groen : styles.rood}`}
                            onClick={() => handleGebruikerActief(g.id, !g.actief)}>
                            {g.actief ? 'Deactiveren' : 'Activeren'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── RAPPORTAGES ── */}
      {tab === 'rapportages' && rapportages && (
        <div>
          <div className={styles.statGrid}>
            <div className={styles.statKaart}>
              <div className={styles.statGetal}>{organisaties.length}</div>
              <div className={styles.statLabel}>Organisaties</div>
            </div>
            <div className={styles.statKaart}>
              <div className={styles.statGetal}>{rapportages.stats.reduce((s, o) => s + o.actieve_gebruikers, 0)}</div>
              <div className={styles.statLabel}>Actieve gebruikers</div>
            </div>
            <div className={styles.statKaart}>
              <div className={styles.statGetal}>{rapportages.stats.reduce((s, o) => s + o.totaal_outputs, 0)}</div>
              <div className={styles.statLabel}>Totaal outputs</div>
            </div>
            <div className={styles.statKaart}>
              <div className={styles.statGetal}>{rapportages.verlopenBinnenkort.length}</div>
              <div className={styles.statLabel}>Licenties verlopen binnenkort</div>
            </div>
          </div>
          <h3 className={styles.sectTitel}>Per organisatie</h3>
          <div className={styles.tabelWrap}>
            <table className={styles.tabel}>
              <thead><tr>
                <th>Organisatie</th><th>Licentie</th><th>Actieve gebruikers</th>
                <th>Sessies</th><th>Outputs</th><th>Laatste activiteit</th><th>Licentie verloopt</th>
              </tr></thead>
              <tbody>
                {rapportages.stats.map(org => (
                  <tr key={org.id}>
                    <td className={styles.orgNaamTekst}>{org.naam}</td>
                    <td><span className={styles.licentieBadge}>{LICENTIE_LABELS[org.licentie_type]}</span></td>
                    <td className={styles.getal}>{org.actieve_gebruikers}</td>
                    <td className={styles.getal}>{org.totaal_sessies}</td>
                    <td className={styles.getal}>{org.totaal_outputs}</td>
                    <td className={styles.datum}>{org.laatste_activiteit ? new Date(org.laatste_activiteit).toLocaleDateString('nl-NL') : '—'}</td>
                    <td className={styles.datum}>{org.licentie_einde ? new Date(org.licentie_einde).toLocaleDateString('nl-NL') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── DREMPELWAARDEN ── */}
      {tab === 'drempelwaarden' && (
        <div>
          <div className={styles.filterBalk} style={{marginBottom:16}}>
            <select value={drempelOrg}
              onChange={e => { setDrempelOrg(e.target.value); laadDrempelwaarden(e.target.value); }}
              className={styles.filterSelect}>
              <option value="">Platformstandaard</option>
              {organisaties.map(o => <option key={o.id} value={o.id}>{o.naam}</option>)}
            </select>
            {!drempelOrg && <span style={{fontSize:13,color:'#999',fontFamily:'Arial'}}>
              Platformstandaard geldt voor alle organisaties zonder eigen instelling
            </span>}
          </div>
          {drempelLaden ? <div className={styles.laadTekst}>Laden...</div> : (
            <div className={styles.tabelWrap}>
              <table className={styles.tabel}>
                <thead><tr>
                  <th>Veld</th><th>Kritisch laag</th><th>Laag</th>
                  <th>Hoog</th><th>Kritisch hoog</th><th>Bron</th><th>Acties</th>
                </tr></thead>
                <tbody>
                  {drempelwaarden.map(d => (
                    <DrempelRij key={d.veld} d={d}
                      heeftOrg={!!drempelOrg}
                      onOpslaan={w => handleDrempelOpslaan(d.veld, w)}
                      onReset={() => handleDrempelReset(d.veld)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!drempelLaden && drempelwaarden.length === 0 && (
            <div style={{textAlign:'center',color:'#999',padding:32,fontFamily:'Arial'}}>
              Kies een organisatie of klik op platformstandaard om drempelwaarden te zien.
            </div>
          )}
        </div>
      )}

      {/* ── MODULES ── */}
      {tab === 'modules' && (
        <div>
          <div className={styles.sectie}>
            <h2 className={styles.sectieTitel}>Modules per organisatie</h2>
            <p style={{fontSize:13,color:'#888',fontFamily:'Arial',marginBottom:16}}>
              Selecteer een organisatie om de actieve modules te beheren. Modules bepalen welke functies beschikbaar zijn binnen de licentie.
            </p>
            <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap',alignItems:'center'}}>
              <select className={styles.invoer} style={{maxWidth:280}}
                value={modulesOrg}
                onChange={e => { setModulesOrg(e.target.value); laadOrgModules(e.target.value); }}>
                <option value="">Kies een organisatie...</option>
                {organisaties.map(o => <option key={o.id} value={o.id}>{o.naam}</option>)}
              </select>
              {modulesOrg && (
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {['basis','professioneel','enterprise','onderwijs'].map(l => (
                    <button key={l} className={styles.knopKlein}
                      onClick={() => pasLicentieToe(modulesOrg, l)}
                      style={{textTransform:'capitalize'}}>
                      {l} toepassen
                    </button>
                  ))}
                </div>
              )}
            </div>

            {modulesOrg && modulesLaden && <div className={styles.laadTekst}>Laden...</div>}
            {modulesOrg && !modulesLaden && orgModules.length > 0 && (
              <div className={styles.tabelWrap}>
                <table className={styles.tabel}>
                  <thead><tr>
                    <th>Module</th><th>Omschrijving</th><th>Actief</th>
                  </tr></thead>
                  <tbody>
                    {orgModules.map(m => (
                      <tr key={m.code}>
                        <td style={{fontWeight:600,fontSize:13}}>{m.naam}</td>
                        <td style={{fontSize:13,color:'#666'}}>{m.omschrijving}</td>
                        <td>
                          <div
                            onClick={() => toggleModule(modulesOrg, m.code, m.actief)}
                            style={{
                              width:40,height:22,borderRadius:11,cursor:'pointer',
                              background: m.actief ? '#3B5FC0' : '#D0D7E3',
                              position:'relative',transition:'background 0.2s',
                            }}>
                            <div style={{
                              position:'absolute',top:3,
                              left: m.actief ? 21 : 3,
                              width:16,height:16,borderRadius:'50%',
                              background:'#fff',transition:'left 0.2s',
                              boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── UPDATES ── */}
      {tab === 'updates' && (
        <div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
            <button className={styles.knopPrimair} onClick={() => {
              setToonUpdateForm(true);
              setUpdateBewerkId(null);
              setUpdateForm({ versie:'', titel:'', omschrijving:'', datum: new Date().toISOString().slice(0,10) });
            }}>+ Nieuwe update</button>
          </div>

          {toonUpdateForm && (
            <div className={styles.sectie} style={{marginBottom:20,padding:20,border:'1.5px solid #E0E7F0',borderRadius:12}}>
              <h3 style={{margin:'0 0 16px',fontSize:15,color:'#2D2B6B',fontFamily:'Arial'}}>
                {updateBewerkId ? 'Update bewerken' : 'Nieuwe update'}
              </h3>
              <div className={styles.tweeKolommen}>
                <div className={styles.veld}>
                  <label>Versienummer</label>
                  <input className={styles.invoer} value={updateForm.versie}
                    onChange={e => setUpdateForm({...updateForm, versie: e.target.value})}
                    placeholder="bijv. 1.2.0" />
                </div>
                <div className={styles.veld}>
                  <label>Datum</label>
                  <input type="date" className={styles.invoer} value={updateForm.datum}
                    onChange={e => setUpdateForm({...updateForm, datum: e.target.value})} />
                </div>
              </div>
              <div className={styles.veld}>
                <label>Titel</label>
                <input className={styles.invoer} value={updateForm.titel}
                  onChange={e => setUpdateForm({...updateForm, titel: e.target.value})}
                  placeholder="bijv. Sessies & AI instructies" />
              </div>
              <div className={styles.veld}>
                <label>Omschrijving</label>
                <textarea className={styles.invoer} rows={3} value={updateForm.omschrijving}
                  onChange={e => setUpdateForm({...updateForm, omschrijving: e.target.value})}
                  placeholder="Beschrijf de wijzigingen in deze versie..." />
              </div>
              <div className={styles.modalKnoppen}>
                <button className={styles.knopSecundair} onClick={() => setToonUpdateForm(false)}>Annuleren</button>
                <button className={styles.knopPrimair} onClick={handleUpdateOpslaan}>Opslaan</button>
              </div>
            </div>
          )}

          {updateLaden ? (
            <div className={styles.laadTekst}>Laden...</div>
          ) : (
            <div className={styles.tabelWrap}>
              <table className={styles.tabel}>
                <thead><tr>
                  <th>Versie</th><th>Datum</th><th>Titel</th><th>Acties</th>
                </tr></thead>
                <tbody>
                  {updateLijst.map(u => (
                    <tr key={u.id}>
                      <td><span style={{background:'#EEF2FF',color:'#2D2B6B',padding:'2px 8px',borderRadius:10,fontSize:12,fontWeight:700}}>{u.versie}</span></td>
                      <td style={{fontSize:13,color:'#666'}}>{new Date(u.datum).toLocaleDateString('nl-NL')}</td>
                      <td style={{fontSize:13,color:'#1A1A2E',fontWeight:600}}>{u.titel}</td>
                      <td>
                        <div style={{display:'flex',gap:6}}>
                          <button className={styles.knopKlein} onClick={() => {
                            setUpdateBewerkId(u.id);
                            setUpdateForm({ versie:u.versie, titel:u.titel, omschrijving:u.omschrijving, datum:u.datum.slice(0,10) });
                            setToonUpdateForm(true);
                          }}>Bewerken</button>
                          <button className={`${styles.knopKlein} ${styles.rood}`}
                            onClick={() => handleUpdateVerwijderen(u.id)}>Verwijderen</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── AI INSTRUCTIES ── */}
      {tab === 'ai-instructies' && (
        <AIInstructies organisaties={organisaties} />
      )}

      {/* ── PLATFORM ── */}
      {tab === 'platform' && (
        <div className={styles.platformWrap}>
          <div className={styles.instellingKaart}>
            <h3 className={styles.instellingTitel}>Platformbanner</h3>
            <p className={styles.instellingUitleg}>Toon een banner aan alle ingelogde gebruikers.</p>
            {banner.actief && banner.tekst && (
              <div className={styles.bannerPreview}>📢 {banner.tekst}</div>
            )}
            <div className={styles.veld}>
              <label>Bannertekst</label>
              <input type="text" value={banner.tekst}
                onChange={e => setBanner({ ...banner, tekst: e.target.value })}
                placeholder="bijv. Gepland onderhoud op 25 april van 22:00 tot 00:00" />
            </div>
            <div className={styles.bannerKnoppen}>
              <label className={styles.toggleLabel}>
                <input type="checkbox" checked={banner.actief}
                  onChange={e => setBanner({ ...banner, actief: e.target.checked })} />
                Banner activeren
              </label>
              <button className={styles.knopPrimair} onClick={handleBanner} disabled={bannerLaden}>
                {bannerLaden ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: organisatie aanmaken/bewerken */}
      {toonModal && (
        <div className={styles.overlay} onClick={() => setToonModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitel}>{geselecteerde ? `${geselecteerde.naam} bewerken` : 'Nieuwe organisatie'}</h2>
            <form onSubmit={handleOpslaan} className={styles.form}>
              <div className={styles.tweeKolommen}>
                <div className={styles.veld}>
                  <label>Naam *</label>
                  <input required value={form.naam}
                    onChange={e => setForm({ ...form, naam: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'') })}
                    placeholder="Naam organisatie" />
                </div>
                <div className={styles.veld}>
                  <label>Slug *</label>
                  <input required value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="url-vriendelijke-naam" />
                </div>
              </div>
              <div className={styles.tweeKolommen}>
                <div className={styles.veld}>
                  <label>Licentietype</label>
                  <select value={form.licentie_type} onChange={e => setForm({ ...form, licentie_type: e.target.value })}>
                    <option value="basis">Basis</option>
                    <option value="professioneel">Professioneel</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="onderwijs">Onderwijs</option>
                  </select>
                </div>
                <div className={styles.veld}>
                  <label>Hiërarchie</label>
                  <select value={form.hierarchie} onChange={e => setForm({ ...form, hierarchie: e.target.value })}>
                    <option value="plat">Plat (Org → User)</option>
                    <option value="locatie">Locaties</option>
                    <option value="afdeling">Afdelingen</option>
                  </select>
                </div>
              </div>
              <div className={styles.drieKolommen}>
                <div className={styles.veld}><label>Max. gebruikers</label>
                  <input type="number" min="1" value={form.max_gebruikers} onChange={e => setForm({ ...form, max_gebruikers: e.target.value })} /></div>
                <div className={styles.veld}><label>Max. outputs/mnd</label>
                  <input type="number" min="1" value={form.max_outputs_pm} onChange={e => setForm({ ...form, max_outputs_pm: e.target.value })} /></div>
                <div className={styles.veld}><label>Max. locaties</label>
                  <input type="number" min="1" value={form.max_locaties} onChange={e => setForm({ ...form, max_locaties: e.target.value })} /></div>
              </div>
              <div className={styles.tweeKolommen}>
                <div className={styles.veld}><label>Licentie start</label>
                  <input type="date" value={form.licentie_start} onChange={e => setForm({ ...form, licentie_start: e.target.value })} /></div>
                <div className={styles.veld}><label>Licentie einde</label>
                  <input type="date" value={form.licentie_einde} onChange={e => setForm({ ...form, licentie_einde: e.target.value })} /></div>
              </div>
              <div className={styles.veld}>
                <label>Bewaartermijn outputs (dagen)</label>
                <input type="number" min="1" max="3650" value={form.bewaartermijn}
                  onChange={e => setForm({ ...form, bewaartermijn: e.target.value })}
                  placeholder="90" />
                <small style={{color:'#999', fontSize:11}}>Aantal dagen dat outputs bewaard blijven. Standaard: 90 dagen.</small>
              </div>
              <div className={styles.veld}>
                <label>Primaire kleur</label>
                <div className={styles.kleurRij}>
                  <input type="color" value={form.primaire_kleur} onChange={e => setForm({ ...form, primaire_kleur: e.target.value })} className={styles.kleurPicker} />
                  <input type="text" value={form.primaire_kleur} onChange={e => setForm({ ...form, primaire_kleur: e.target.value })} placeholder="#1B4F8A" style={{ maxWidth:120 }} />
                </div>
              </div>
              <div className={styles.modalKnoppen}>
                <button type="button" className={styles.knopSecundair} onClick={() => setToonModal(false)}>Annuleren</button>
                <button type="submit" className={styles.knopPrimair}>{geselecteerde ? 'Opslaan' : 'Aanmaken'}</button>
              </div>
            </form>
            {geselecteerde && (
              <div className={styles.veld} style={{marginTop:16,paddingTop:16,borderTop:'1px solid #E0E7F0'}}>
                <label style={{fontSize:13,fontWeight:600,color:'#1A1A2E',fontFamily:'Arial',display:'block',marginBottom:8}}>Logo organisatie</label>
                <LogoUpload
                  organisatieId={geselecteerde.id}
                  huidigeLogo={geselecteerde.logo_url}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal: API-sleutel */}
      {apiOrg && (
        <div className={styles.overlay} onClick={() => setApiOrg(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()} style={{ maxWidth:440 }}>
            <h2 className={styles.modalTitel}>OpenAI API-sleutel — {apiOrg.naam}</h2>
            <p style={{ fontSize:13, color:'#666', marginBottom:16, fontFamily:'Arial' }}>
              De sleutel wordt versleuteld opgeslagen en is nooit leesbaar terug te halen.
            </p>
            <div className={styles.veld}>
              <label>API-sleutel</label>
              <input type="password" value={apiSleutel} onChange={e => setApiSleutel(e.target.value)} placeholder="sk-..." autoFocus />
            </div>
            <div className={styles.modalKnoppen} style={{ marginTop:16 }}>
              <button className={styles.knopSecundair} onClick={() => setApiOrg(null)}>Annuleren</button>
              <button className={styles.knopPrimair} onClick={() => handleApiSleutel(apiOrg.id)}>Opslaan</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: wachtwoord reset */}
      {resetModal && (
        <div className={styles.overlay} onClick={() => setResetModal(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()} style={{ maxWidth:440 }}>
            <h2 className={styles.modalTitel}>Wachtwoord resetten</h2>
            <p style={{ fontSize:13, color:'#666', marginBottom:16, fontFamily:'Arial' }}>
              Nieuw wachtwoord instellen voor <strong>{resetModal.email}</strong>
            </p>
            <form onSubmit={handleWachtwoordReset}>
              <div className={styles.veld}>
                <label>Nieuw wachtwoord</label>
                <input type="text" value={nieuwWachtwoord} onChange={e => setNieuwWachtwoord(e.target.value)}
                  placeholder="Minimaal 8 tekens" autoFocus required minLength={8} />
              </div>
              <div className={styles.modalKnoppen} style={{ marginTop:16 }}>
                <button type="button" className={styles.knopSecundair} onClick={() => setResetModal(null)}>Annuleren</button>
                <button type="submit" className={styles.knopPrimair}>Opslaan</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
